import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Building2, FileText, AlertCircle, Loader2, CreditCard } from 'lucide-react';
import SignaturePad from '../components/SignaturePad';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public quote-signing page used by the client after receiving a signed link
 * from an admin. No authentication required — validates via the secret token.
 */
export default function QuoteSignPublic() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signed, setSigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentLinkUrl, setPaymentLinkUrl] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/quotes/${token}`)
      .then(({ data }) => {
        setQuote(data);
        if (data.signed_at) setSigned(true);
        if (data.payment_link_url) setPaymentLinkUrl(data.payment_link_url);
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Lien invalide ou expiré'))
      .finally(() => setLoading(false));
  }, [token]);

  const onSign = async (dataUrl, name) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/public/quotes/${token}/sign`, {
        signatory_name: name,
        signature_b64: dataUrl,
      });
      setSigned(true);
      if (data?.payment_link_url) setPaymentLinkUrl(data.payment_link_url);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de la signature');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen bg-[#FAFAFE] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#4F46E5]" /></div>;

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFE] flex items-center justify-center p-6">
        <div className="soft-card p-8 max-w-md text-center" data-testid="quote-sign-error">
          <AlertCircle className="w-12 h-12 text-[#EF4444] mx-auto mb-3" />
          <h1 className="text-[18px] font-bold text-[#1A1B2E] mb-1">Lien non valide</h1>
          <p className="text-[13px] text-[#6B6C85]">{error}</p>
        </div>
      </div>
    );
  }

  const fmtEur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`;

  return (
    <div className="min-h-screen bg-[#FAFAFE] py-10 px-4" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="quote-sign-page">
      <div className="max-w-2xl mx-auto">
        <header className="mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl brand-gradient flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-[11.5px] uppercase tracking-wider text-[#8E8FA5] font-semibold">Signature électronique</div>
            <h1 className="text-[20px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Devis {quote.id}</h1>
          </div>
        </header>

        <section className="soft-card p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#4F46E5]" />
              <span className="text-[13.5px] font-semibold text-[#1A1B2E]">{quote.client}</span>
            </div>
            <div className="text-[11.5px] text-[#8E8FA5]">Valide jusqu'au {quote.validUntil}</div>
          </div>

          <div className="text-[13px] text-[#4A4B63] mb-3">
            <strong>Programme :</strong> {quote.program || '—'}
          </div>

          {(quote.lines || []).length > 0 && (
            <div className="border rounded-xl border-[#EFF0F6] overflow-hidden mb-3">
              <table className="w-full text-[12.5px]">
                <thead className="bg-[#FAFAFE] text-[#6B6C85]">
                  <tr><th className="text-left px-3 py-2">Désignation</th><th className="text-right px-3 py-2">Qté</th><th className="text-right px-3 py-2">PU HT</th><th className="text-right px-3 py-2">Total HT</th></tr>
                </thead>
                <tbody>
                  {quote.lines.map((ln, i) => (
                    <tr key={i} className="border-t border-[#F3F4F8]"><td className="px-3 py-2">{ln.label}</td><td className="px-3 py-2 text-right">{ln.quantity}</td><td className="px-3 py-2 text-right">{fmtEur(ln.unit_price)}</td><td className="px-3 py-2 text-right font-semibold">{fmtEur(ln.quantity * ln.unit_price)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="space-y-1 text-[13px]">
            {quote.discount > 0 && <div className="flex justify-between text-[#D97706]"><span>Remise</span><span>-{fmtEur(quote.discount)}</span></div>}
            <div className="flex justify-between text-[#6B6C85]"><span>Total HT</span><span>{fmtEur(quote.amount_ht)}</span></div>
            <div className="flex justify-between text-[#6B6C85]"><span>TVA ({quote.tva_rate}%)</span><span>{fmtEur(quote.tva_amount)}</span></div>
            <div className="flex justify-between pt-2 border-t border-[#EFF0F6] text-[15px] font-bold text-[#1A1B2E]"><span>Total TTC</span><span className="text-[#4F46E5]">{fmtEur(quote.amount)}</span></div>
          </div>
        </section>

        {signed ? (
          <div className="soft-card p-8 text-center" data-testid="quote-signed-ok">
            <CheckCircle2 className="w-14 h-14 text-[#14B8A6] mx-auto mb-3" />
            <h2 className="text-[19px] font-bold text-[#1A1B2E] mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Devis signé avec succès</h2>
            <p className="text-[13px] text-[#6B6C85]">Merci ! Votre devis {quote.id} a été accepté par {quote.signatory_name || 'vous'}. Un PDF signé vous sera envoyé par email.</p>
            {paymentLinkUrl && !quote.payment_link_paid_at && (
              <div className="mt-6 pt-5 border-t border-[#EFF0F6]" data-testid="quote-payment-link-block">
                <div className="text-[12.5px] uppercase tracking-wider text-[#635BFF] font-semibold mb-1.5 flex items-center justify-center gap-1.5">
                  <CreditCard className="w-3.5 h-3.5" /> Régler dès maintenant
                </div>
                <p className="text-[12.5px] text-[#4A4B63] mb-3">
                  Vous pouvez régler votre devis dès à présent en ligne (paiement sécurisé via Stripe) — votre formateur recevra une confirmation immédiate.
                </p>
                <a
                  data-testid="quote-pay-cta"
                  href={paymentLinkUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-xl text-white text-[14px] font-semibold transition-colors"
                  style={{ background: '#635BFF' }}
                >
                  <CreditCard className="w-4 h-4" />
                  Régler {fmtEur(quote.amount)} en ligne
                </a>
              </div>
            )}
            {quote.payment_link_paid_at && (
              <div className="mt-6 pt-5 border-t border-[#EFF0F6]" data-testid="quote-paid-block">
                <div className="text-[12.5px] uppercase tracking-wider text-[#0F766E] font-semibold mb-1.5 flex items-center justify-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Paiement reçu
                </div>
                <p className="text-[12.5px] text-[#4A4B63]">
                  Votre paiement a bien été enregistré le {new Date(quote.payment_link_paid_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}.
                </p>
              </div>
            )}
          </div>
        ) : (
          <section className="soft-card p-6">
            <h2 className="text-[15px] font-semibold text-[#1A1B2E] mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Accepter et signer ce devis</h2>
            <p className="text-[12px] text-[#6B6C85] mb-4">En signant ci-dessous, vous acceptez les termes du devis ci-dessus.</p>
            <SignaturePad initialName={quote.client || ''} onSave={onSign} onCancel={() => {}} />
            {submitting && <div className="mt-2 text-[12px] text-[#4F46E5] flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…</div>}
          </section>
        )}

        <footer className="mt-6 text-center text-[11px] text-[#8E8FA5]">
          Signature hébergée par <strong>{quote.org?.name || 'OFManager'}</strong> · Lien sécurisé unique
        </footer>
      </div>
    </div>
  );
}
